import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress, Typography } from "@mui/material";

import { JITSI_DOMAIN, JITSI_SCRIPT_URL } from "../config/jitsi";

const JITSI_SCRIPT = JITSI_SCRIPT_URL;
const DOMAIN = JITSI_DOMAIN;

/** Full toolbar for tutors/moderators (still no invite). */
const TUTOR_TOOLBAR = [
  "microphone",
  "camera",
  "desktop",
  "fullscreen",
  "fodeviceselection",
  "hangup",
  "chat",
  "raisehand",
  "tileview",
  "settings",
  "videoquality",
  "filmstrip",
  "participants-pane",
];

/**
 * Student toolbar: no way to browse other classmates (no tile view, filmstrip, or roster).
 * Feels like a 1:1 with the tutor.
 */
const STUDENT_TOOLBAR = [
  "microphone",
  "camera",
  "desktop",
  "fullscreen",
  "fodeviceselection",
  "hangup",
  "chat",
  "raisehand",
  "settings",
  "videoquality",
];

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

function waitForJitsiAPI(maxMs = 3000, intervalMs = 50) {
  return new Promise((resolve) => {
    if (window.JitsiMeetExternalAPI) {
      resolve(window.JitsiMeetExternalAPI);
      return;
    }
    const start = Date.now();
    const id = setInterval(() => {
      if (window.JitsiMeetExternalAPI) {
        clearInterval(id);
        resolve(window.JitsiMeetExternalAPI);
      } else if (Date.now() - start >= maxMs) {
        clearInterval(id);
        resolve(null);
      }
    }, intervalMs);
  });
}

function buildToolbarButtons(canRecord, soloWithTutor) {
  const buttons = soloWithTutor ? [...STUDENT_TOOLBAR] : [...TUTOR_TOOLBAR];
  if (canRecord) {
    buttons.push("recording", "localrecording");
  }
  return buttons;
}

function isModeratorParticipant(p) {
  if (!p) return false;
  if (p.moderator === true) return true;
  const role = String(p.role || "").toLowerCase();
  return role === "moderator" || role === "owner";
}

/**
 * Pin the classroom moderator so the student stage shows only the tutor.
 * channelLastN: 1 already limits remote video to one stream.
 */
function pinTalkingTutor(api) {
  try {
    api.executeCommand("setTileView", false);
  } catch (e) {
    // ignore
  }

  let participants = [];
  try {
    participants = api.getParticipantsInfo() || [];
  } catch (e) {
    return;
  }

  const tutor =
    participants.find((p) => isModeratorParticipant(p) && !p.local) ||
    participants.find((p) => isModeratorParticipant(p));

  if (tutor?.participantId) {
    try {
      api.executeCommand("pinParticipant", tutor.participantId);
    } catch (e) {
      // ignore
    }
  }
}

export function leaveMeetingToPortal(returnPath = "/") {
  try {
    window.close();
  } catch (e) {
    // ignore
  }
  window.setTimeout(() => {
    if (!window.closed) {
      window.location.assign(returnPath);
    }
  }, 150);
}

/**
 * Embeds self-hosted Jitsi Meet.
 *
 * soloWithTutor: students only see themselves + the pinned tutor (no classmates).
 * Tutors keep the full participant list / filmstrip / tile view.
 */
export default function JitsiMeet({
  roomCode,
  displayName = "Tutor",
  height = 500,
  onReady,
  onLeave,
  canRecord = false,
  autoStartRecording = false,
  recordingMode = "local",
  /** Full RTMPS destination for Cloudflare Stream Live (url + key). */
  rtmpDestination = null,
  jwt = null,
  soloWithTutor = false,
}) {
  const containerRef = useRef(null);
  const apiRef = useRef(null);
  const startedRecordingRef = useRef(false);
  const leftRef = useRef(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [recordingNote, setRecordingNote] = useState(null);

  useEffect(() => {
    if (!roomCode) return;

    let mounted = true;
    let initTimer = null;
    let pinTimer = null;
    startedRecordingRef.current = false;
    leftRef.current = false;
    setLoading(true);
    setError(null);
    setRecordingNote(null);

    const tryStartRecording = (api) => {
      if (!mounted || !canRecord || !autoStartRecording || startedRecordingRef.current) return;

      const mode =
        recordingMode === "stream" && rtmpDestination
          ? "stream"
          : recordingMode === "file"
            ? "file"
            : "local";

      const payload =
        mode === "stream"
          ? { mode: "stream", rtmpStreamKey: rtmpDestination, onlySelf: false }
          : { mode, onlySelf: false };

      const start = () => {
        // Do not show success until recordingStatusChanged { on: true }.
        // Meet often rejects with "recording service is not enabled" asynchronously.
        setRecordingNote(
          mode === "file"
            ? "Starting server recording…"
            : mode === "stream"
              ? "Starting cloud stream recording…"
              : "Starting local recording…"
        );

        const fail = (reason) => {
          startedRecordingRef.current = false;
          setRecordingNote(
            `Recording failed: ${reason || "service not available"}. Enable Jibri on meet.11plusmagic.co.uk (fileRecordingsEnabled), then retry Record.`
          );
        };

        try {
          let result;
          if (typeof api.startRecording === "function") {
            result = api.startRecording(payload);
          } else {
            result = api.executeCommand("startRecording", payload);
          }
          // Mark attempt only; success comes from recordingStatusChanged.
          startedRecordingRef.current = true;
          if (result && typeof result.then === "function") {
            result.catch((err) => {
              const msg =
                (err && (err.message || err.error || String(err))) ||
                "the recording service is not enabled";
              fail(msg);
            });
          }
          // If Jibri never starts, clear the optimistic “starting…” note.
          setTimeout(() => {
            if (!mounted) return;
            setRecordingNote((current) =>
              current && String(current).startsWith("Starting")
                ? "Recording did not start. On the Meet server enable Jibri + fileRecordingsEnabled, then use Record."
                : current
            );
          }, 12000);
        } catch (e) {
          fail(e?.message || "Could not start recording");
        }
      };

      // Jibri only accepts startRecording once this participant is a Meet moderator.
      const attempt = (triesLeft) => {
        if (!mounted || startedRecordingRef.current) return;
        let participants = [];
        try {
          participants = api.getParticipantsInfo() || [];
        } catch (e) {
          participants = [];
        }
        const local = participants.find((p) => p.local) || participants[0];
        const isMod =
          local &&
          (local.moderator === true ||
            String(local.role || "").toLowerCase() === "moderator" ||
            String(local.role || "").toLowerCase() === "owner");

        if (isMod || mode === "local" || triesLeft <= 0) {
          start();
          return;
        }
        setTimeout(() => attempt(triesLeft - 1), 1000);
      };

      attempt(15);
    };

    loadScript(JITSI_SCRIPT)
      .then(() => waitForJitsiAPI())
      .then((JitsiAPI) => {
        if (!mounted) {
          setLoading(false);
          return;
        }
        if (!JitsiAPI) {
          setError("Jitsi Meet failed to load. Please refresh.");
          setLoading(false);
          return;
        }
        initTimer = setTimeout(() => {
          if (!mounted || !containerRef.current) {
            setLoading(false);
            return;
          }
          try {
            const toolbar = buildToolbarButtons(canRecord, soloWithTutor);
            const options = {
              roomName: roomCode,
              parentNode: containerRef.current,
              width: "100%",
              height: "100%",
              userInfo: { displayName },
              configOverwrite: {
                prejoinConfig: { enabled: false },
                prejoinPageEnabled: false,
                enableClosePage: false,
                disableDeepLinking: true,
                disableInviteFunctions: true,
                hiddenPremeetingButtons: ["invite"],
                localRecording: {
                  disable: !canRecord,
                  enabled: Boolean(canRecord),
                  notifyAllParticipants: false,
                  disableSelfRecording: !canRecord,
                },
                fileRecordingsEnabled: Boolean(canRecord && (recordingMode === "file" || recordingMode === "stream")),
                fileRecordingsServiceEnabled: Boolean(canRecord && recordingMode === "file"),
                fileRecordingsServiceSharingEnabled: false,
                liveStreamingEnabled: Boolean(canRecord && recordingMode === "stream"),
                toolbarButtons: toolbar,
                dropbox: { appKey: undefined },
                ...(soloWithTutor
                  ? {
                      // Only one remote video → the pinned tutor feels like a 1:1 call.
                      channelLastN: 1,
                      startWithTileView: false,
                      disableTileView: true,
                      disableParticipantModeSwitch: true,
                      filmstrip: {
                        disabled: true,
                        disableResizable: true,
                        disableStageFilmstrip: true,
                      },
                      notifications: [],
                    }
                  : {
                      channelLastN: -1,
                    }),
              },
              interfaceConfigOverwrite: {
                TOOLBAR_BUTTONS: toolbar,
                HIDE_INVITE_MORE_HEADER: true,
                DISABLE_JOIN_LEAVE_NOTIFICATIONS: soloWithTutor,
                ...(soloWithTutor
                  ? {
                      DISABLE_FOCUS_INDICATOR: true,
                      FILM_STRIP_MAX_HEIGHT: 0,
                      VERTICAL_FILMSTRIP: false,
                    }
                  : {}),
              },
            };

            if (jwt) {
              options.jwt = jwt;
            }

            const api = new JitsiAPI(DOMAIN, options);
            apiRef.current = api;

            if (!canRecord) {
              const blockRecording = () => {
                try {
                  api.executeCommand("stopRecording", "local");
                  api.executeCommand("stopRecording", "file");
                } catch (e) {
                  // ignore
                }
              };
              api.addListener("recordingStatusChanged", (status) => {
                if (status?.on) blockRecording();
              });
            }

            const handleLeave = () => {
              if (!mounted || leftRef.current) return;
              leftRef.current = true;
              try {
                api.dispose();
              } catch (e) {
                // ignore
              }
              apiRef.current = null;
              if (onLeave) onLeave();
            };

            api.addListener("readyToClose", handleLeave);

            api.addListener("videoConferenceJoined", () => {
              setTimeout(() => tryStartRecording(api), 1500);
              if (soloWithTutor) {
                pinTalkingTutor(api);
                // Tutor may join a moment later — keep re-pinning briefly.
                pinTimer = setInterval(() => {
                  if (!mounted) return;
                  pinTalkingTutor(api);
                }, 2000);
                setTimeout(() => {
                  if (pinTimer) clearInterval(pinTimer);
                  pinTimer = null;
                }, 30000);
              }
            });

            if (soloWithTutor) {
              api.addListener("participantJoined", () => pinTalkingTutor(api));
              api.addListener("participantRoleChanged", () => pinTalkingTutor(api));
            }

            api.addListener("recordingStatusChanged", (status) => {
              if (!mounted || !status) return;
              if (status.on) {
                setRecordingNote(
                  status.mode === "stream"
                    ? "Recording in progress (Cloudflare Stream)."
                    : status.mode === "file"
                      ? "Recording in progress (Jibri → will upload to R2 on end)."
                      : "Recording in progress (saving to this device)."
                );
              } else if (status.error) {
                startedRecordingRef.current = false;
                setRecordingNote(
                  `Recording error: ${status.error}. Enable Jibri on the Meet server (fileRecordingsEnabled), then use Record.`
                );
              } else if (status.on === false) {
                // Stopped or never started — don't leave a false “started” banner.
                setRecordingNote((current) =>
                  current && String(current).startsWith("Starting")
                    ? "Recording is not running (service disabled or Jibri unavailable)."
                    : current
                );
              }
            });

            api.addListener("errorOccurred", (e) => {
              if (!mounted || !e) return;
              const name = String(e.name || e.error?.name || "");
              const msg = String(e.message || e.error?.message || e.error || "");
              if (
                /recording/i.test(name) ||
                /recording/i.test(msg) ||
                /recording service is not enabled/i.test(msg)
              ) {
                startedRecordingRef.current = false;
                setRecordingNote(
                  `Recording failed: ${msg || name || "service not enabled"}. Fix Jibri on meet.11plusmagic.co.uk.`
                );
              }
            });

            if (onReady) onReady(api);
          } catch (e) {
            if (mounted) setError(e.message || "Failed to start meeting");
          }
          setLoading(false);
        }, 100);
      })
      .catch((err) => {
        if (mounted) {
          setError(err.message || "Failed to load meeting");
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
      if (initTimer) clearTimeout(initTimer);
      if (pinTimer) clearInterval(pinTimer);
      if (apiRef.current) {
        try {
          apiRef.current.dispose();
        } catch (e) {
          // ignore
        }
        apiRef.current = null;
      }
    };
  }, [roomCode, displayName, onReady, onLeave, canRecord, autoStartRecording, recordingMode, rtmpDestination, jwt, soloWithTutor]);

  if (!roomCode) {
    return (
      <Box sx={{ height, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "grey.100", borderRadius: 2 }}>
        <Typography color="text.secondary">Start or select a class to load the meeting.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", height, borderRadius: 2, overflow: "hidden", bgcolor: "#1a1a2e" }}>
      {loading && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.7)", zIndex: 1 }}>
          <CircularProgress sx={{ color: "white" }} />
          <Typography sx={{ ml: 2, color: "white" }}>Loading meeting…</Typography>
        </Box>
      )}
      {error && (
        <Box sx={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "rgba(0,0,0,0.7)", zIndex: 1 }}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {recordingNote && !error && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            right: 8,
            zIndex: 2,
            px: 1.5,
            py: 0.75,
            borderRadius: 1,
            bgcolor: "rgba(0,0,0,0.65)",
            color: "white",
            typography: "caption",
            pointerEvents: "none",
          }}
        >
          {recordingNote}
        </Box>
      )}
      <Box ref={containerRef} sx={{ width: "100%", height: "100%", minHeight: 400 }} />
    </Box>
  );
}
